import { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";

import "@/i18n";
import "@/index.css";

import Layout from "@/components/layout/Layout";
import Home from "@/pages/Home";
import BhtOne from "@/pages/BhtOne";
import Platform from "@/pages/Platform";
import WhyBhtOne from "@/pages/WhyBhtOne";
import ModulePage from "@/pages/ModulePage";
import ModulesIndex from "@/pages/ModulesIndex";
import Knowledge from "@/pages/Knowledge";
import Article from "@/pages/Article";
import Contact from "@/pages/Contact";
import BookDemo from "@/pages/BookDemo";
import NotFound from "@/pages/NotFound";
import IndexHubPage from "@/pages/IndexHubPage";
import TrustPage from "@/pages/TrustPage";
import SolutionPage from "@/pages/SolutionPage";
import IndustryPage from "@/pages/IndustryPage";
import InteractiveDemo from "@/pages/InteractiveDemo";
import AdminEmails from "@/pages/AdminEmails";
import Pricing from "@/pages/Pricing";
import Company from "@/pages/Company";
import Manifesto from "@/pages/Manifesto";
import { CaseStudiesIndex, CaseStudyPage } from "@/pages/CaseStudies";
import { Careers, Media, Investors, Partners, Community } from "@/pages/TrustPages";
import NewsletterConfirm from "@/pages/NewsletterConfirm";
import ComingSoon from "@/pages/ComingSoon";
import { Privacy, Terms, Cookies } from "@/pages/Legal";
import PreviewBadge from "@/components/preview/PreviewBadge";
import { TRUST_INDEX, SOLUTIONS_INDEX, INDUSTRIES_INDEX } from "@/data/pages";

/**
 * PUBLIC SITE MODE — runtime-driven via `/api/config`.
 *
 * The frontend fetches `{coming_soon_mode, site_mode}` at boot from the
 * backend so a single backend env var flip switches the whole site — no
 * frontend rebuild required.
 *
 * `REACT_APP_PUBLIC_SITE_MODE` is kept as a build-time fallback used only
 * when `/api/config` is unreachable (network error, cold start, etc.).
 *
 * When mode = "coming-soon":
 *   • Public visitors see the ComingSoon page.
 *   • A valid server-issued HttpOnly session cookie unlocks the full site
 *     (see /api/preview/login). The React SPA checks status via /api/preview/status.
 *   • Legal pages remain publicly reachable so they can be linked from the footer.
 *   • /:lang/newsletter/confirm and /:lang/admin/emails also remain reachable —
 *     admin has its own auth (ADMIN_TOKEN header), never inherited from preview.
 *
 * When mode = "full-site": everything is public.
 */
const BUILD_SITE_MODE = (process.env.REACT_APP_PUBLIC_SITE_MODE || "full-site").toLowerCase();
const API = process.env.REACT_APP_BACKEND_URL;

function useSiteConfig() {
  // `mode` is null while we're loading; once resolved it holds a string.
  const [mode, setMode] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/config`);
        if (!r.ok) throw new Error("bad status");
        const data = await r.json();
        const m = (data?.site_mode || BUILD_SITE_MODE).toLowerCase();
        if (!cancelled) setMode(m);
      } catch {
        // Fall back to build-time env so the site still renders even if
        // the API is temporarily unreachable.
        if (!cancelled) setMode(BUILD_SITE_MODE);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { mode, loading: mode === null };
}

function usePreviewStatus() {
  const [state, setState] = useState({ loading: true, active: false });

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/preview/status`, { credentials: "include" });
      const data = await r.json();
      setState({ loading: false, active: !!data.active });
    } catch {
      setState({ loading: false, active: false });
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  return { ...state, refresh };
}

function LangSync() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  useEffect(() => {
    if (lang && ["en", "nl"].includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    if (typeof document !== "undefined" && lang) {
      document.documentElement.lang = lang;
    }
  }, [lang, i18n]);
  return null;
}

function TrustIndex() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  return (
    <IndexHubPage
      eyebrow="Trust Center"
      title={lang === "nl" ? "Enterprise-grade vertrouwen." : "Enterprise-grade trust."}
      sub={lang === "nl" ? "Security, privacy, verantwoorde AI en compliance — met bewijs, niet met beloften." : "Security, privacy, responsible AI and compliance — with evidence, not promises."}
      basePath="/trust"
      items={TRUST_INDEX}
      testIdPrefix="trust-index"
      seoTitle="Trust Center"
    />
  );
}
function SolutionsIndex() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  return (
    <IndexHubPage
      eyebrow="Solutions"
      title={lang === "nl" ? "Voor elke vorm van hospitality." : "For every shape of hospitality."}
      sub={lang === "nl" ? "Herbruikbare enterprise-templates die BHT One vertalen naar uw property." : "Reusable enterprise templates that translate BHT One into your property."}
      basePath="/solutions"
      items={SOLUTIONS_INDEX}
      testIdPrefix="solutions-index"
      seoTitle="Solutions"
    />
  );
}
function IndustriesIndex() {
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  return (
    <IndexHubPage
      eyebrow="Industries"
      title={lang === "nl" ? "Hospitality in vier gedaanten." : "Hospitality in four shapes."}
      sub={lang === "nl" ? "Hotels, resorts, vakantieparken en apartments — één besturingssysteem, ander tempo per industrie." : "Hotels, resorts, holiday parks and apartments — one operating system, different tempo per industry."}
      basePath="/industries"
      items={INDUSTRIES_INDEX}
      testIdPrefix="industries-index"
      seoTitle="Industries"
    />
  );
}

function LangRoutes() {
  return (
    <>
      <LangSync />
      <Layout>
        <Routes>
          <Route index element={<Home />} />
          <Route path="bht-one" element={<BhtOne />} />
          <Route path="platform" element={<Platform />} />
          <Route path="why-bht-one" element={<WhyBhtOne />} />
          <Route path="modules" element={<ModulesIndex />} />
          <Route path="modules/:slug" element={<ModulePage />} />
          <Route path="knowledge" element={<Knowledge />} />
          <Route path="knowledge/:slug" element={<Article />} />
          <Route path="trust" element={<TrustIndex />} />
          <Route path="trust/:slug" element={<TrustPage />} />
          <Route path="solutions" element={<SolutionsIndex />} />
          <Route path="solutions/:slug" element={<SolutionPage />} />
          <Route path="industries" element={<IndustriesIndex />} />
          <Route path="industries/:slug" element={<IndustryPage />} />
          <Route path="demo" element={<InteractiveDemo />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="company" element={<Company />} />
          <Route path="manifesto" element={<Manifesto />} />
          <Route path="case-studies" element={<CaseStudiesIndex />} />
          <Route path="case-studies/:slug" element={<CaseStudyPage />} />
          <Route path="careers" element={<Careers />} />
          <Route path="media" element={<Media />} />
          <Route path="investors" element={<Investors />} />
          <Route path="partners" element={<Partners />} />
          <Route path="community" element={<Community />} />
          <Route path="newsletter/confirm" element={<NewsletterConfirm />} />
          <Route path="admin/emails" element={<AdminEmails />} />
          <Route path="legal/privacy" element={<Privacy />} />
          <Route path="legal/terms" element={<Terms />} />
          <Route path="legal/cookies" element={<Cookies />} />
          <Route path="contact" element={<Contact />} />
          <Route path="book-demo" element={<BookDemo />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </>
  );
}

/** Stealth routes — only the small allowlist that must remain reachable during a Coming Soon window. */
function StealthLangSync() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  useEffect(() => {
    if (lang && ["en", "nl"].includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    if (typeof document !== "undefined" && lang) {
      document.documentElement.lang = lang;
    }
  }, [lang, i18n]);
  return null;
}
function NewsletterConfirmStealth() {
  return (
    <div className="min-h-screen bg-bht-void text-white">
      <NewsletterConfirm />
    </div>
  );
}
function StealthLangRoutes({ onPreviewGranted }) {
  return (
    <>
      <StealthLangSync />
      <Routes>
        <Route path="newsletter/confirm" element={<NewsletterConfirmStealth />} />
        <Route path="admin/emails" element={<AdminEmails />} />
        <Route path="legal/privacy" element={<Privacy />} />
        <Route path="legal/terms" element={<Terms />} />
        <Route path="legal/cookies" element={<Cookies />} />
        <Route path="*" element={<ComingSoon onPreviewGranted={onPreviewGranted} />} />
      </Routes>
    </>
  );
}
function StealthRoutes({ onPreviewGranted }) {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/en" replace />} />
      <Route path=":lang/*" element={<StealthLangRoutes onPreviewGranted={onPreviewGranted} />} />
    </Routes>
  );
}

function LoadingSplash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bht-void" data-testid="app-loading">
      <div
        className="h-6 w-6 animate-spin rounded-full border-2 border-bht-gold/30 border-t-bht-gold"
        aria-label="Loading"
        role="status"
      />
    </div>
  );
}

export default function App() {
  const { mode: siteMode, loading: modeLoading } = useSiteConfig();
  const preview = usePreviewStatus();
  const isStealthMode = siteMode === "coming-soon";
  const showFull = !isStealthMode || preview.active;

  // Wait until BOTH the site mode and (if stealth) preview status are known
  // before rendering — prevents a "flash of coming soon" for logged-in team
  // members and a "flash of full site" on cold API starts.
  if (modeLoading || (isStealthMode && preview.loading)) return <LoadingSplash />;

  return (
    <HelmetProvider>
      <BrowserRouter>
        {/* Belt-and-braces noindex: applies to any full-site view served during Stealth mode. */}
        {isStealthMode && (
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
        )}
        {showFull ? (
          <Routes>
            <Route path="/" element={<Navigate to="/en" replace />} />
            <Route path=":lang/*" element={<LangRoutes />} />
          </Routes>
        ) : (
          <StealthRoutes onPreviewGranted={preview.refresh} />
        )}
        {isStealthMode && preview.active && <PreviewBadge onExit={preview.refresh} />}
        <Toaster theme="dark" position="bottom-right" richColors closeButton />
      </BrowserRouter>
    </HelmetProvider>
  );
}
